import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Bot, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { Card, EmptyState, Badge, Btn } from "../AdminUI";

function ConfirmModal({ title, body, confirmLabel, onCancel, onConfirm, busy }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="conv-confirm-modal">
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onCancel} />
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-head font-bold text-lg text-[var(--navy)] uppercase">{title}</h2>
          <button type="button" onClick={onCancel} disabled={busy}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-600 whitespace-pre-line">{body}</p>
        <div className="flex gap-2 justify-end">
          <Btn variant="ghost" onClick={onCancel} disabled={busy} data-testid="conv-confirm-cancel">Cancel</Btn>
          <Btn variant="danger" onClick={onConfirm} loading={busy} data-testid="conv-confirm-delete">{confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
}

export default function Conversations() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // { mode: "single"|"bulk", conv? }
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/conversations").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((c) => c.id)));
  const clearSelection = () => setSelected(new Set());

  const doSingleDelete = async (conv) => {
    setBusy(true);
    try {
      await api.delete(`/admin/conversations/${conv.id}`);
      toast.success("Conversation deleted.");
      setItems((prev) => prev.filter((c) => c.id !== conv.id));
      setSelected((prev) => { const n = new Set(prev); n.delete(conv.id); return n; });
      if (active?.id === conv.id) setActive(null);
      setConfirm(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to delete conversation. Please try again.");
    } finally { setBusy(false); }
  };

  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    setBusy(true);
    try {
      const r = await api.post("/admin/conversations/bulk-delete", { ids });
      const { deleted = 0, failed = 0 } = r.data || {};
      if (failed > 0) toast.warning(`${deleted} conversation${deleted === 1 ? "" : "s"} deleted. ${failed} could not be deleted.`);
      else toast.success(`${deleted} conversation${deleted === 1 ? "" : "s"} deleted.`);
      const idSet = new Set(ids);
      setItems((prev) => prev.filter((c) => !idSet.has(c.id)));
      if (active && idSet.has(active.id)) setActive(null);
      clearSelection();
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to delete the selected conversations. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Ghost AI Conversations</h1>

      {items.length === 0 ? (
        <Card><EmptyState icon={MessageSquare} title="No conversations yet" hint="Ghost AI chats will appear here." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
          <Card className="max-h-[72vh] overflow-y-auto">
            {/* Select-all + bulk action bar */}
            <div className="sticky top-0 bg-white z-10 pb-2 mb-1 border-b border-gray-100">
              <label className="flex items-center gap-2 py-1.5 px-1 text-sm font-semibold text-[var(--navy)] cursor-pointer select-none">
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                  className="w-4 h-4 accent-[var(--action)]" data-testid="conv-select-all" />
                Select All
                <span className="text-xs font-normal text-gray-400 ml-auto">{items.length} total</span>
              </label>
              {selected.size > 0 && (
                <div className="flex items-center gap-2 mt-1 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2" data-testid="conv-bulk-bar">
                  <span className="text-xs font-semibold text-red-700" data-testid="conv-selected-count">
                    {selected.size} selected
                  </span>
                  <div className="ml-auto flex gap-1.5">
                    <Btn variant="ghost" className="!py-1 !px-2 !text-xs" onClick={clearSelection} data-testid="conv-clear-selection">Clear</Btn>
                    <Btn variant="danger" className="!py-1 !px-2 !text-xs" onClick={() => setConfirm({ mode: "bulk" })} data-testid="conv-delete-selected">
                      <Trash2 className="w-3.5 h-3.5" /> Delete Selected
                    </Btn>
                  </div>
                </div>
              )}
            </div>

            <div className="divide-y divide-gray-100">
              {items.map((c) => (
                <div key={c.id} className={`flex items-start gap-2 py-2.5 px-1 rounded ${active?.id === c.id ? "bg-gray-50" : "hover:bg-gray-50"}`} data-testid={`conv-row-${c.id}`}>
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleOne(c.id)}
                    onClick={(e) => e.stopPropagation()} className="mt-1 w-4 h-4 accent-[var(--action)] shrink-0"
                    data-testid={`conv-check-${c.id}`} aria-label="Select conversation" />
                  <button onClick={() => setActive(c)} className="flex-1 text-left min-w-0" data-testid={`conv-open-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm text-[var(--navy)] truncate">{c.lead_id || "No lead yet"}</span>
                      {c.lead_id && <Badge color="green">Lead</Badge>}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{c.messages?.length || 0} messages · {c.landing_page || "/"}</p>
                  </button>
                  <button onClick={() => setConfirm({ mode: "single", conv: c })} title="Delete conversation"
                    aria-label="Delete conversation" className="p-2 text-gray-300 hover:text-red-500 shrink-0"
                    data-testid={`conv-delete-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </Card>

          <Card title={active ? `Transcript ${active.lead_id || ""}` : "Select a conversation"}>
            {!active ? <EmptyState icon={Bot} title="Select a conversation to view the transcript." /> : (
              <div className="space-y-3 max-h-[62vh] overflow-y-auto" data-testid="conv-transcript">
                {active.messages?.map((m, i) => (
                  <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--action)] text-white" : "bg-gray-100 text-[var(--text-dark)]"}`}>
                      <span className="block text-[10px] opacity-60 mb-0.5">{m.role === "user" ? "Customer" : "Ghost AI"}</span>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {confirm?.mode === "single" && (
        <ConfirmModal
          title="Delete Conversation?"
          body={"This will permanently delete this Ghost AI conversation and its transcript.\n\nLinked leads, customers, and other CRM records will NOT be deleted."}
          confirmLabel="Delete"
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={() => doSingleDelete(confirm.conv)}
        />
      )}
      {confirm?.mode === "bulk" && (
        <ConfirmModal
          title={`Delete ${selected.size} Conversation${selected.size === 1 ? "" : "s"}?`}
          body={"This will permanently delete the selected Ghost AI conversations and their transcripts.\n\nLinked leads, customers, and other CRM records will NOT be deleted."}
          confirmLabel={`Delete ${selected.size} Conversation${selected.size === 1 ? "" : "s"}`}
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={doBulkDelete}
        />
      )}
    </div>
  );
}
